import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css, Global } from '@emotion/react';
import type { GridProps, GridItemProps } from './Grid.types';

/**
 * Spacing map based on production theme
 */
const gapMap = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

/**
 * Alignment map for grid items
 */
const alignMap = {
  start: 'start',
  center: 'center',
  end: 'end',
  stretch: 'stretch',
};

/**
 * Justify map for grid content
 */
const justifyMap = {
  start: 'start',
  center: 'center',
  end: 'end',
  'space-between': 'space-between',
  'space-around': 'space-around',
};

/**
 * Base grid styles
 */
const StyledGrid = styled.div<{
  $gap: keyof typeof gapMap;
  $align: keyof typeof alignMap;
  $justify: keyof typeof justifyMap;
  $isResponsive: boolean;
}>`
  display: grid;
  width: 100%;
  gap: ${({ $gap }) => gapMap[$gap]};
  align-items: ${({ $align }) => alignMap[$align]};
  justify-content: ${({ $justify }) => justifyMap[$justify]};
`;

/**
 * Grid item styles
 */
const StyledGridItem = styled.div<{
  $isResponsive: boolean;
}>`
  min-width: 0;
  min-height: 0;
`;

/**
 * Global responsive styles for grid
 * These are needed because CSS-in-JS doesn't support data attribute media queries well
 */
const responsiveGridStyles = css`
  /* Extra small (xs) - 0px+ */
  [data-columns-xs="1"] { grid-template-columns: repeat(1, 1fr) !important; }
  [data-columns-xs="2"] { grid-template-columns: repeat(2, 1fr) !important; }
  [data-columns-xs="3"] { grid-template-columns: repeat(3, 1fr) !important; }
  [data-columns-xs="4"] { grid-template-columns: repeat(4, 1fr) !important; }
  [data-columns-xs="5"] { grid-template-columns: repeat(5, 1fr) !important; }
  [data-columns-xs="6"] { grid-template-columns: repeat(6, 1fr) !important; }
  [data-columns-xs="7"] { grid-template-columns: repeat(7, 1fr) !important; }
  [data-columns-xs="8"] { grid-template-columns: repeat(8, 1fr) !important; }
  [data-columns-xs="9"] { grid-template-columns: repeat(9, 1fr) !important; }
  [data-columns-xs="10"] { grid-template-columns: repeat(10, 1fr) !important; }
  [data-columns-xs="11"] { grid-template-columns: repeat(11, 1fr) !important; }
  [data-columns-xs="12"] { grid-template-columns: repeat(12, 1fr) !important; }

  /* Small (sm) - 640px+ */
  @media (min-width: 640px) {
    [data-columns-sm="1"] { grid-template-columns: repeat(1, 1fr) !important; }
    [data-columns-sm="2"] { grid-template-columns: repeat(2, 1fr) !important; }
    [data-columns-sm="3"] { grid-template-columns: repeat(3, 1fr) !important; }
    [data-columns-sm="4"] { grid-template-columns: repeat(4, 1fr) !important; }
    [data-columns-sm="5"] { grid-template-columns: repeat(5, 1fr) !important; }
    [data-columns-sm="6"] { grid-template-columns: repeat(6, 1fr) !important; }
    [data-columns-sm="7"] { grid-template-columns: repeat(7, 1fr) !important; }
    [data-columns-sm="8"] { grid-template-columns: repeat(8, 1fr) !important; }
    [data-columns-sm="9"] { grid-template-columns: repeat(9, 1fr) !important; }
    [data-columns-sm="10"] { grid-template-columns: repeat(10, 1fr) !important; }
    [data-columns-sm="11"] { grid-template-columns: repeat(11, 1fr) !important; }
    [data-columns-sm="12"] { grid-template-columns: repeat(12, 1fr) !important; }
  }

  /* Medium (md) - 768px+ */
  @media (min-width: 768px) {
    [data-columns-md="1"] { grid-template-columns: repeat(1, 1fr) !important; }
    [data-columns-md="2"] { grid-template-columns: repeat(2, 1fr) !important; }
    [data-columns-md="3"] { grid-template-columns: repeat(3, 1fr) !important; }
    [data-columns-md="4"] { grid-template-columns: repeat(4, 1fr) !important; }
    [data-columns-md="5"] { grid-template-columns: repeat(5, 1fr) !important; }
    [data-columns-md="6"] { grid-template-columns: repeat(6, 1fr) !important; }
    [data-columns-md="7"] { grid-template-columns: repeat(7, 1fr) !important; }
    [data-columns-md="8"] { grid-template-columns: repeat(8, 1fr) !important; }
    [data-columns-md="9"] { grid-template-columns: repeat(9, 1fr) !important; }
    [data-columns-md="10"] { grid-template-columns: repeat(10, 1fr) !important; }
    [data-columns-md="11"] { grid-template-columns: repeat(11, 1fr) !important; }
    [data-columns-md="12"] { grid-template-columns: repeat(12, 1fr) !important; }
  }

  /* Large (lg) - 1024px+ */
  @media (min-width: 1024px) {
    [data-columns-lg="1"] { grid-template-columns: repeat(1, 1fr) !important; }
    [data-columns-lg="2"] { grid-template-columns: repeat(2, 1fr) !important; }
    [data-columns-lg="3"] { grid-template-columns: repeat(3, 1fr) !important; }
    [data-columns-lg="4"] { grid-template-columns: repeat(4, 1fr) !important; }
    [data-columns-lg="5"] { grid-template-columns: repeat(5, 1fr) !important; }
    [data-columns-lg="6"] { grid-template-columns: repeat(6, 1fr) !important; }
    [data-columns-lg="7"] { grid-template-columns: repeat(7, 1fr) !important; }
    [data-columns-lg="8"] { grid-template-columns: repeat(8, 1fr) !important; }
    [data-columns-lg="9"] { grid-template-columns: repeat(9, 1fr) !important; }
    [data-columns-lg="10"] { grid-template-columns: repeat(10, 1fr) !important; }
    [data-columns-lg="11"] { grid-template-columns: repeat(11, 1fr) !important; }
    [data-columns-lg="12"] { grid-template-columns: repeat(12, 1fr) !important; }
  }

  /* Extra large (xl) - 1280px+ */
  @media (min-width: 1280px) {
    [data-columns-xl="1"] { grid-template-columns: repeat(1, 1fr) !important; }
    [data-columns-xl="2"] { grid-template-columns: repeat(2, 1fr) !important; }
    [data-columns-xl="3"] { grid-template-columns: repeat(3, 1fr) !important; }
    [data-columns-xl="4"] { grid-template-columns: repeat(4, 1fr) !important; }
    [data-columns-xl="5"] { grid-template-columns: repeat(5, 1fr) !important; }
    [data-columns-xl="6"] { grid-template-columns: repeat(6, 1fr) !important; }
    [data-columns-xl="7"] { grid-template-columns: repeat(7, 1fr) !important; }
    [data-columns-xl="8"] { grid-template-columns: repeat(8, 1fr) !important; }
    [data-columns-xl="9"] { grid-template-columns: repeat(9, 1fr) !important; }
    [data-columns-xl="10"] { grid-template-columns: repeat(10, 1fr) !important; }
    [data-columns-xl="11"] { grid-template-columns: repeat(11, 1fr) !important; }
    [data-columns-xl="12"] { grid-template-columns: repeat(12, 1fr) !important; }
  }

  /* Responsive column spans for Grid.Item */
  /* Extra small (xs) - 0px+ */
  [data-col-span-xs="1"] { grid-column: span 1 !important; }
  [data-col-span-xs="2"] { grid-column: span 2 !important; }
  [data-col-span-xs="3"] { grid-column: span 3 !important; }
  [data-col-span-xs="4"] { grid-column: span 4 !important; }
  [data-col-span-xs="5"] { grid-column: span 5 !important; }
  [data-col-span-xs="6"] { grid-column: span 6 !important; }
  [data-col-span-xs="7"] { grid-column: span 7 !important; }
  [data-col-span-xs="8"] { grid-column: span 8 !important; }
  [data-col-span-xs="9"] { grid-column: span 9 !important; }
  [data-col-span-xs="10"] { grid-column: span 10 !important; }
  [data-col-span-xs="11"] { grid-column: span 11 !important; }
  [data-col-span-xs="12"] { grid-column: span 12 !important; }

  /* Small (sm) - 640px+ */
  @media (min-width: 640px) {
    [data-col-span-sm="1"] { grid-column: span 1 !important; }
    [data-col-span-sm="2"] { grid-column: span 2 !important; }
    [data-col-span-sm="3"] { grid-column: span 3 !important; }
    [data-col-span-sm="4"] { grid-column: span 4 !important; }
    [data-col-span-sm="5"] { grid-column: span 5 !important; }
    [data-col-span-sm="6"] { grid-column: span 6 !important; }
    [data-col-span-sm="7"] { grid-column: span 7 !important; }
    [data-col-span-sm="8"] { grid-column: span 8 !important; }
    [data-col-span-sm="9"] { grid-column: span 9 !important; }
    [data-col-span-sm="10"] { grid-column: span 10 !important; }
    [data-col-span-sm="11"] { grid-column: span 11 !important; }
    [data-col-span-sm="12"] { grid-column: span 12 !important; }
  }

  /* Medium (md) - 768px+ */
  @media (min-width: 768px) {
    [data-col-span-md="1"] { grid-column: span 1 !important; }
    [data-col-span-md="2"] { grid-column: span 2 !important; }
    [data-col-span-md="3"] { grid-column: span 3 !important; }
    [data-col-span-md="4"] { grid-column: span 4 !important; }
    [data-col-span-md="5"] { grid-column: span 5 !important; }
    [data-col-span-md="6"] { grid-column: span 6 !important; }
    [data-col-span-md="7"] { grid-column: span 7 !important; }
    [data-col-span-md="8"] { grid-column: span 8 !important; }
    [data-col-span-md="9"] { grid-column: span 9 !important; }
    [data-col-span-md="10"] { grid-column: span 10 !important; }
    [data-col-span-md="11"] { grid-column: span 11 !important; }
    [data-col-span-md="12"] { grid-column: span 12 !important; }
  }

  /* Large (lg) - 1024px+ */
  @media (min-width: 1024px) {
    [data-col-span-lg="1"] { grid-column: span 1 !important; }
    [data-col-span-lg="2"] { grid-column: span 2 !important; }
    [data-col-span-lg="3"] { grid-column: span 3 !important; }
    [data-col-span-lg="4"] { grid-column: span 4 !important; }
    [data-col-span-lg="5"] { grid-column: span 5 !important; }
    [data-col-span-lg="6"] { grid-column: span 6 !important; }
    [data-col-span-lg="7"] { grid-column: span 7 !important; }
    [data-col-span-lg="8"] { grid-column: span 8 !important; }
    [data-col-span-lg="9"] { grid-column: span 9 !important; }
    [data-col-span-lg="10"] { grid-column: span 10 !important; }
    [data-col-span-lg="11"] { grid-column: span 11 !important; }
    [data-col-span-lg="12"] { grid-column: span 12 !important; }
  }

  /* Extra large (xl) - 1280px+ */
  @media (min-width: 1280px) {
    [data-col-span-xl="1"] { grid-column: span 1 !important; }
    [data-col-span-xl="2"] { grid-column: span 2 !important; }
    [data-col-span-xl="3"] { grid-column: span 3 !important; }
    [data-col-span-xl="4"] { grid-column: span 4 !important; }
    [data-col-span-xl="5"] { grid-column: span 5 !important; }
    [data-col-span-xl="6"] { grid-column: span 6 !important; }
    [data-col-span-xl="7"] { grid-column: span 7 !important; }
    [data-col-span-xl="8"] { grid-column: span 8 !important; }
    [data-col-span-xl="9"] { grid-column: span 9 !important; }
    [data-col-span-xl="10"] { grid-column: span 10 !important; }
    [data-col-span-xl="11"] { grid-column: span 11 !important; }
    [data-col-span-xl="12"] { grid-column: span 12 !important; }
  }
`;

/**
 * Grid component - A flexible CSS Grid layout component
 *
 * @example
 * ```tsx
 * // Fixed 3 columns
 * <Grid columns={3} gap="md">
 *   <Card>...</Card>
 *   <Card>...</Card>
 *   <Card>...</Card>
 * </Grid>
 *
 * // Responsive columns
 * <Grid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap="lg">
 *   {items.map(item => <Card key={item.id}>...</Card>)}
 * </Grid>
 *
 * // Auto-fit responsive
 * <Grid autoFit minColumnWidth="200px" gap="md">
 *   {items.map(item => <Card key={item.id}>...</Card>)}
 * </Grid>
 *
 * // With explicit grid items
 * <Grid columns={4} gap="md">
 *   <Grid.Item colSpan={2}>Wide item</Grid.Item>
 *   <Grid.Item>Normal item</Grid.Item>
 *   <Grid.Item>Normal item</Grid.Item>
 * </Grid>
 * ```
 */
export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      children,
      columns = 1,
      gap = 'md',
      align = 'stretch',
      justify = 'start',
      autoFit = false,
      minColumnWidth = '250px',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const isResponsive = typeof columns === 'object';
    
    // Build inline styles for auto-fit mode or fixed columns
    const inlineStyles: React.CSSProperties = {};
    
    if (autoFit) {
      inlineStyles.gridTemplateColumns = `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`;
    } else if (typeof columns === 'number') {
      inlineStyles.gridTemplateColumns = `repeat(${columns}, 1fr)`;
    }

    return (
      <>
        {isResponsive && <Global styles={responsiveGridStyles} />}
        <StyledGrid
          ref={ref}
          className={className}
          style={inlineStyles}
          $gap={gap}
          $align={align}
          $justify={justify}
          $isResponsive={isResponsive}
          data-gap={gap}
          data-align={align}
          data-justify={justify}
          data-columns-xs={isResponsive ? columns.xs : undefined}
          data-columns-sm={isResponsive ? columns.sm : undefined}
          data-columns-md={isResponsive ? columns.md : undefined}
          data-columns-lg={isResponsive ? columns.lg : undefined}
          data-columns-xl={isResponsive ? columns.xl : undefined}
          data-testid={dataTestId || testId}
          data-id={dataId}
        >
          {children}
        </StyledGrid>
      </>
    );
  }
);

Grid.displayName = 'Grid';

/**
 * Grid.Item component - A grid item with optional column/row spanning
 *
 * @example
 * ```tsx
 * <Grid columns={4}>
 *   <Grid.Item colSpan={2}>Wide item</Grid.Item>
 *   <Grid.Item rowSpan={2}>Tall item</Grid.Item>
 *   <Grid.Item>Normal item</Grid.Item>
 * </Grid>
 * ```
 */
export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(
  (
    {
      children,
      colSpan,
      rowSpan,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const isResponsiveColSpan = typeof colSpan === 'object';
    
    // Build inline styles for fixed spans
    const inlineStyles: React.CSSProperties = {};
    
    if (typeof colSpan === 'number') {
      inlineStyles.gridColumn = `span ${colSpan}`;
    }
    
    if (rowSpan) {
      inlineStyles.gridRow = `span ${rowSpan}`;
    }

    return (
      <>
        {isResponsiveColSpan && <Global styles={responsiveGridStyles} />}
        <StyledGridItem
          ref={ref}
          className={className}
          style={inlineStyles}
          $isResponsive={isResponsiveColSpan}
          data-col-span-xs={isResponsiveColSpan ? colSpan.xs : undefined}
          data-col-span-sm={isResponsiveColSpan ? colSpan.sm : undefined}
          data-col-span-md={isResponsiveColSpan ? colSpan.md : undefined}
          data-col-span-lg={isResponsiveColSpan ? colSpan.lg : undefined}
          data-col-span-xl={isResponsiveColSpan ? colSpan.xl : undefined}
          data-row-span={rowSpan}
          data-testid={dataTestId || testId}
          data-id={dataId}
        >
          {children}
        </StyledGridItem>
      </>
    );
  }
);

GridItem.displayName = 'Grid.Item';

// Compound component pattern
const GridWithItem = Object.assign(Grid, {
  Item: GridItem,
});

export default GridWithItem;